import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../lib/supabase';

import { Tabs, Tab } from '@mui/material';

function Categories() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [categoriesInUse, setCategoriesInUse] = useState(new Set());
  const [tab, setTab] = useState(0);

  // Formulário para categoria principal
  const [mainForm, setMainForm] = useState({
    name: '',
    expense_type: ''
  });

  // Formulário para subcategoria
  const [subForm, setSubForm] = useState({
    name: '',
    expense_type: '',
    parent_id: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      // Buscar categorias com informações da categoria pai
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('personal_categories')
        .select(`
          *,
          parent:parent_id (id, name)
        `)
        .eq('type', 'saida')
        .order('name');

      if (categoriesError) throw categoriesError;

      // Buscar transações ativas (não arquivadas) para cada categoria
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('personal_transactions')
        .select('category_id')
        .is('deleted_at', null)
        .not('category_id', 'is', null);

      if (transactionsError) throw transactionsError;

      // Buscar subcategorias para validar exclusão
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from('personal_categories')
        .select('parent_id')
        .not('parent_id', 'is', null);

      if (subcategoriesError) throw subcategoriesError;

      // Criar um Set com os IDs das categorias em uso por transações ativas
      const inUse = new Set(transactionsData.map(t => t.category_id));
      setCategoriesInUse(inUse);
      
      // Criar um Set com os IDs das categorias que têm subcategorias
      const hasSubcategories = new Set(subcategoriesData.map(s => s.parent_id));
      
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Erro ao carregar categorias');
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Removido: handleOpenDialog, handleSubmit e setFormData/setFormData
  // Não são mais necessários pois cada modal já tem seu próprio estado e lógica.

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        // Verificar se existem transações ativas usando esta categoria
        const { data: transactions, error: checkError } = await supabase
          .from('personal_transactions')
          .select('id')
          .eq('category_id', id)
          .is('deleted_at', null)
          .limit(1);

        if (checkError) throw checkError;

        if (transactions && transactions.length > 0) {
          showSnackbar('Não é possível excluir esta categoria pois existem transações ativas associadas a ela');
          return;
        }

        const { error } = await supabase
          .from('personal_categories')
          .delete()
          .eq('id', id);

        if (error) {
          if (error.code === '23503') { // Código de violação de chave estrangeira
            showSnackbar('Não é possível excluir esta categoria pois existem transações ativas associadas a ela');
          } else {
            throw error;
          }
          return;
        }

        showSnackbar('Categoria excluída com sucesso');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        showSnackbar('Erro ao excluir categoria: ' + error.message);
      }
    }
  };

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  // Corrige: handleCloseDialog para uso nos modais
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Tabs 
        value={tab} 
        onChange={(_, v) => setTab(v)} 
        sx={{ mb: 3 }}
        variant={isMobile ? "fullWidth" : "standard"}
        centered={!isMobile}
      >
        <Tab label="Categorias Principais" />
        <Tab label="Subcategorias" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'flex-start' : 'space-between', 
            alignItems: isMobile ? 'stretch' : 'center', 
            gap: isMobile ? 2 : 0,
            mb: 3 
          }}>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              Categorias Principais
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingCategory(null);
                setMainForm({ name: '', expense_type: '' });
                setOpenDialog('main');
              }}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Nova Categoria Principal
            </Button>
          </Box>
          
          {isMobile ? (
            <Grid container spacing={2}>
              {categories.filter(cat => !cat.parent_id).map((category) => (
                <Grid item xs={12} key={category.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {category.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setEditingCategory(category);
                              setMainForm({
                                name: category.name,
                                expense_type: category.expense_type || ''
                              });
                              setOpenDialog('main');
                            }}
                            sx={{ minWidth: '44px', minHeight: '44px' }}
                          >
                            <EditIcon />
                          </IconButton>
                          <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(category.id)}
                                disabled={categoriesInUse.has(category.id)}
                                sx={{ minWidth: '44px', minHeight: '44px' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      {category.expense_type && (
                        <Chip 
                          label={category.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                          size="small"
                          color={category.expense_type === 'fixed' ? 'primary' : 'info'}
                          variant="outlined"
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.filter(cat => !cat.parent_id).map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {category.expense_type && (
                            <Chip 
                              label={category.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                              size="small"
                              color={category.expense_type === 'fixed' ? 'primary' : 'info'}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingCategory(category);
                            setMainForm({
                              name: category.name,
                              expense_type: category.expense_type || ''
                            });
                            setOpenDialog('main');
                          }}
                          sx={{ minWidth: '44px', minHeight: '44px' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(category.id)}
                              disabled={categoriesInUse.has(category.id)}
                              sx={{ minWidth: '44px', minHeight: '44px' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'flex-start' : 'space-between', 
            alignItems: isMobile ? 'stretch' : 'center', 
            gap: isMobile ? 2 : 0,
            mb: 3 
          }}>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              Subcategorias
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingCategory(null);
                setSubForm({ name: '', expense_type: '', parent_id: '' });
                setOpenDialog('sub');
              }}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Nova Subcategoria
            </Button>
          </Box>
          
          {isMobile ? (
            <Grid container spacing={2}>
              {categories.filter(cat => cat.parent_id).map((category) => {
                const parentCategory = categories.find(c => c.id === category.parent_id);
                return (
                  <Grid item xs={12} key={category.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" component="div">
                              {category.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {parentCategory?.name || ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setEditingCategory(category);
                                setSubForm({
                                  name: category.name,
                                  expense_type: category.expense_type || '',
                                  parent_id: category.parent_id
                                });
                                setOpenDialog('sub');
                              }}
                              sx={{ minWidth: '44px', minHeight: '44px' }}
                            >
                              <EditIcon />
                            </IconButton>
                            <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDelete(category.id)}
                                  disabled={categoriesInUse.has(category.id)}
                                  sx={{ minWidth: '44px', minHeight: '44px' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                        {parentCategory?.expense_type && (
                          <Chip 
                            label={parentCategory.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                            size="small"
                            color={parentCategory.expense_type === 'fixed' ? 'primary' : 'info'}
                            variant="outlined"
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Categoria Principal</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.filter(cat => cat.parent_id).map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        {categories.find(c => c.id === category.parent_id)?.name || ''}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {(() => {
                            const parentCategory = categories.find(c => c.id === category.parent_id);
                            return parentCategory?.expense_type && (
                              <Chip 
                                label={parentCategory.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                                size="small"
                                color={parentCategory.expense_type === 'fixed' ? 'primary' : 'info'}
                                variant="outlined"
                              />
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingCategory(category);
                            setSubForm({
                              name: category.name,
                              expense_type: category.expense_type || '',
                              parent_id: category.parent_id
                            });
                            setOpenDialog('sub');
                          }}
                          sx={{ minWidth: '44px', minHeight: '44px' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(category.id)}
                              disabled={categoriesInUse.has(category.id)}
                              sx={{ minWidth: '44px', minHeight: '44px' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Modal Categoria Principal */}
      <Dialog 
        open={openDialog === 'main'} 
        onClose={handleCloseDialog} 
        maxWidth={isMobile ? false : "sm"}
        fullWidth
        fullScreen={isMobile}
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            const categoryData = {
              name: mainForm.name,
              expense_type: mainForm.expense_type,
              parent_id: null,
              type: 'saida'
            };
            if (editingCategory) {
              supabase
                .from('personal_categories')
                .update(categoryData)
                .eq('id', editingCategory.id)
                .then(({ error }) => {
                  if (error) showSnackbar('Erro ao atualizar categoria');
                  else showSnackbar('Categoria atualizada com sucesso');
                  handleCloseDialog();
                  fetchCategories();
                });
            } else {
              supabase
                .from('personal_categories')
                .insert([categoryData])
                .then(({ error }) => {
                  if (error) showSnackbar('Erro ao criar categoria');
                  else showSnackbar('Categoria criada com sucesso');
                  handleCloseDialog();
                  fetchCategories();
                });
            }
          }}
        >
          <DialogTitle>
            {editingCategory ? 'Editar Categoria Principal' : 'Nova Categoria Principal'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="name"
                label="Nome"
                value={mainForm.name}
                onChange={e => setMainForm({ ...mainForm, name: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Tipo de Despesa</InputLabel>
                <Select
                  name="expense_type"
                  value={mainForm.expense_type}
                  onChange={e => setMainForm({ ...mainForm, expense_type: e.target.value })}
                  label="Tipo de Despesa"
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  <MenuItem value="fixed">Fixa</MenuItem>
                  <MenuItem value="variable">Variável</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal Subcategoria */}
      <Dialog 
        open={openDialog === 'sub'} 
        onClose={handleCloseDialog} 
        maxWidth={isMobile ? false : "sm"}
        fullWidth
        fullScreen={isMobile}
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            const categoryData = {
              name: subForm.name,
              parent_id: subForm.parent_id,
              type: 'saida'
            };
            if (editingCategory) {
              supabase
                .from('personal_categories')
                .update(categoryData)
                .eq('id', editingCategory.id)
                .then(({ error }) => {
                  if (error) showSnackbar('Erro ao atualizar subcategoria');
                  else showSnackbar('Subcategoria atualizada com sucesso');
                  handleCloseDialog();
                  fetchCategories();
                });
            } else {
              supabase
                .from('personal_categories')
                .insert([categoryData])
                .then(({ error }) => {
                  if (error) showSnackbar('Erro ao criar subcategoria');
                  else showSnackbar('Subcategoria criada com sucesso');
                  handleCloseDialog();
                  fetchCategories();
                });
            }
          }}
        >
          <DialogTitle>
            {editingCategory ? 'Editar Subcategoria' : 'Nova Subcategoria'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="name"
                label="Nome"
                value={subForm.name}
                onChange={e => setSubForm({ ...subForm, name: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Categoria Principal</InputLabel>
                <Select
                  name="parent_id"
                  value={subForm.parent_id}
                  onChange={e => setSubForm({ ...subForm, parent_id: e.target.value })}
                  label="Categoria Principal"
                  required
                >
                  <MenuItem value="">
                    <em>Selecione uma Categoria Principal</em>
                  </MenuItem>
                  {categories
                    .filter(cat => !cat.parent_id)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
}

export default Categories;
